import * as ngCommonHttp from '@angular/common/http';

import { Injectable } from '@angular/core';
import { XenialService as generatedXenialService } from '@generated/tcea/services/xenial.service';
import { BehaviorSubject, Observable, throwError, of, forkJoin, Subject } from 'rxjs';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { StoreService } from '@services/store/1.0/store.service';
import { StoreView } from '@interfaces/store/1.0';
import { XenialReportingCategoriesGet$Params } from '@generated/tcea/fn/xenial/xenial-reporting-categories-get';
import {
  PricePutRequests,
  ReportingCategoriesResult,
  ReportingCategory,
  StoreSite,
  StoreSites,
  XenialCatalogEntry,
  XenialCatalogResponse,
} from '@generated/tcea/models';
import { XenialCatalogsCatalogEntryIdPut$Params } from '@generated/tcea/fn/xenial/xenial-catalogs-catalog-entry-id-put';
import { XenialCatalogsCatalogEntryIdGet$Params } from '@generated/tcea/fn/xenial/xenial-catalogs-catalog-entry-id-get';
import { XenialStoresCompanyIdGet$Params } from '@generated/tcea/fn/xenial/xenial-stores-company-id-get';
import { Association, AssociationLookupType, AssociationType } from '@interfaces/association/1.0';
import { AssociationService } from '@services/association/1.0/association.service';
import { InstructionService } from '@services/instruction/1.0/instruction.service';
import { CatalogItem } from '@interfaces/catalog/1.0/catalog-item';
import { TranslateService } from '@ngx-translate/core';
import { CatalogPricesService } from '@generated/tcea/services/catalog-prices.service';
import { CatalogsPricesCatalogEntryIdGet$Params } from '@generated/tcea/fn/catalog-prices/catalogs-prices-catalog-entry-id-get';
import { CatalogsPricesPut$Params } from '@generated/tcea/fn/catalog-prices/catalogs-prices-put';
import { MenuAvailabilityService } from '@generated/tcea/services/menu-availability.service';
import { CatalogEntriesCatalogEntryIdAvailabilitiesGet$Params } from '@generated/tcea/fn/menu-availability/catalog-entries-catalog-entry-id-availabilities-get';
import {
  AbstractXenialService,
  XenialSyncStatusResult,
} from '@abstract-services/xenial/1.0/xenial.abstract-service';

@Injectable({
  providedIn: 'root',
})
export class XenialService extends AbstractXenialService {
  /**
   * Updates availabilities for stores using MenuAvailabilityService
   * @param updates Array of AvailabilitiesPatchInput objects
   * @param brandId The brand/tenant ID
   * @returns Observable<any> (API response)
   */
  updateAvailabilities(
    updates: Array<{
      available: boolean;
      catalogEntryId: string;
      storeId: string;
      instructionId?: string;
    }>,
    brandId: string,
  ): Observable<any> {
    const params = {
      'Active-Tenant-Identifier': brandId,
      AvailabilitiesPatchInput: updates,
      integrations: 'xenial',
    };
    return this.menuAvailabilityService.availabilitiesPatch(params).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }
  private xenialDataGroupCache: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private addInstructionIdCache: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);

  constructor(
    private generatedXenialService: generatedXenialService,
    private storeService: StoreService,
    private associationService: AssociationService,
    private instructionService: InstructionService,
    private translateService: TranslateService,
    private catalogPricesService: CatalogPricesService,
    private menuAvailabilityService: MenuAvailabilityService,
  ) {
    super();
  }

  /**
   * Gets availabilities for a catalog entry using MenuAvailabilityService
   * @param brandId - The brand/tenant ID
   * @param catalogEntryId - The catalog entry ID
   * @returns Observable<any> (API response)
   */
  getAvailabilitiesForCatalogEntry(brandId: string, catalogEntryId: string): Observable<any> {
    const params: CatalogEntriesCatalogEntryIdAvailabilitiesGet$Params = {
      'Active-Tenant-Identifier': brandId,
      catalogEntryId,
    };
    return this.menuAvailabilityService.catalogEntriesCatalogEntryIdAvailabilitiesGet(params).pipe(
      map((response: any) => {
        // Response shape: { availabilities: [{ storeId, available, ... }] }
        if (response && Array.isArray(response)) {
          return response;
        }
        if (response && response._embedded && Array.isArray(response._embedded.availabilities)) {
          return response._embedded.availabilities;
        }
        return [];
      }),
    );
  }

  /**
   * Finds the Xenial data group for the current tenant using StoreService
   * @param brandId - The brand/tenant ID
   * @returns Observable that emits the Xenial data group or null if not found
   */
  findXenialDataGroup(brandId: string): Observable<any> {
    if (this.xenialDataGroupCache.value) {
      return of(this.xenialDataGroupCache.value);
    }

    return this.storeService.get(brandId).pipe(
      map((stores) => {
        const result = this.processXenialStores(stores);
        this.xenialDataGroupCache.next(result);

        return result;
      }),
      catchError(() => {
        return of(null);
      }),
    );
  }

  /**
   * Processes stores to find the one with Xenial POS system and returns its data group info
   * @param stores - Array of StoreView objects from StoreService
   * @returns The Xenial store's data group info or null if not found
   */
  private processXenialStores(stores: StoreView[]): any {
    const xenialStore = stores.find((store) => {
      return store.posSystem?.toLowerCase() === 'xenial';
    });

    if (!xenialStore) {
      return null;
    }

    return xenialStore.dataGroupId;
  }

  /**
   * Get the cached Xenial data group as Observable
   */
  getXenialDataGroup(): Observable<any> {
    return this.xenialDataGroupCache.asObservable();
  }

  /**
   * Get the cached Xenial data group ID synchronously
   * @returns The cached data group ID or undefined if not found
   */
  getCachedXenialDataGroupId(): string | undefined {
    return this.xenialDataGroupCache.value;
  }

  /**
   * Finds Xenial data group and "Add" instruction ID for modifiers in one call
   * This method combines the data group lookup with instruction lookup for efficiency
   * @param brandId - The brand/tenant ID
   * @returns Observable that emits an object with dataGroupId and addInstructionId
   */
  findXenialDataGroupAndAddInstruction(brandId: string): Observable<{
    dataGroupId: string | null;
    addInstructionId: string | null;
  }> {
    // Check cache first
    const cachedDataGroupId = this.xenialDataGroupCache.value;
    const cachedAddInstructionId = this.addInstructionIdCache.value;

    if (cachedDataGroupId && cachedAddInstructionId) {
      return of({
        dataGroupId: cachedDataGroupId,
        addInstructionId: cachedAddInstructionId,
      });
    }

    return forkJoin({
      dataGroup: this.findXenialDataGroup(brandId),
      instructions: this.instructionService.getInstructions(brandId),
    }).pipe(
      map((result) => {
        const dataGroupId = result.dataGroup;

        const addInstruction = result.instructions?.find(
          (inst: CatalogItem) => inst.name === 'Add',
        );
        const addInstructionId = addInstruction?.catalogEntryId || null;
        this.addInstructionIdCache.next(addInstructionId);

        return {
          dataGroupId,
          addInstructionId,
        };
      }),
      catchError(() => {
        return of({
          dataGroupId: null,
          addInstructionId: null,
        });
      }),
    );
  }

  /**
   * Get the cached "Add" instruction ID synchronously
   * @returns The cached "Add" instruction ID or null if not found
   */
  getCachedAddInstructionId(): string | null {
    return this.addInstructionIdCache.value;
  }

  /**
   * Gets store aggregator summary
   *
   */
  getXenialDetails(brandId: string, catalogEntryId: string): Observable<XenialCatalogResponse> {
    let params: XenialCatalogsCatalogEntryIdGet$Params = {
      'Active-Tenant-Identifier': brandId,
      catalogEntryId,
    };
    return this.generatedXenialService.xenialCatalogsCatalogEntryIdGet(params).pipe(
      map((response: XenialCatalogResponse) => {
        if (response) {
          return response;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets store aggregator summary
   *
   */
  getReportingCategories(brandId: string, xenialBrandId: string): Observable<ReportingCategory[]> {
    let params: XenialReportingCategoriesGet$Params = {
      'Active-Tenant-Identifier': brandId,
      'X-Company-Id': xenialBrandId,
    };
    return this.generatedXenialService.xenialReportingCategoriesGet(params).pipe(
      map((response: ReportingCategoriesResult) => {
        if (response?._embedded?.xenial) {
          const xenialData = response._embedded.xenial;
          return Array.isArray(xenialData) ? xenialData : [xenialData];
        } else if (response && (response as any).reportingCategories) {
          return (response as any).reportingCategories;
        } else {
          return [];
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets the Xenial stores for a company
   *
   * @param companyId The company identifier to get stores for
   * @param brandId The brand identifier (optional)
   * @returns An Observable containing the store sites
   */
  getXenialStores(companyId: string, brandId?: string): Observable<StoreSite[]> {
    let params: XenialStoresCompanyIdGet$Params = {
      'Active-Tenant-Identifier': brandId || '',
      companyId: companyId,
    };
    return this.generatedXenialService.xenialStoresCompanyIdGet(params).pipe(
      map((response: StoreSites) => {
        if (response && response._embedded && Array.isArray(response._embedded.xenial)) {
          return response._embedded.xenial;
        } else {
          return [];
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * save xenial aggregator summary
   *
   */
  save(catalogEntryId: string, brandId: string, body: XenialCatalogEntry): Observable<any> {
    let params: XenialCatalogsCatalogEntryIdPut$Params = {
      'Active-Tenant-Identifier': brandId,
      catalogEntryId,
      body,
    };
    return this.generatedXenialService
      .xenialCatalogsCatalogEntryIdPut(params)
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  /**
   * Determines if a Xenial POS association should be created
   *
   * This method evaluates multiple conditions to determine if a POS association
   * should be created for a catalog entry:
   * 1. Xenial must be enabled for the brand
   * 2. Xenial sync control must be turned ON
   * 3. Xenial details must exist and have content
   * 4. Xenial sync must be explicitly enabled in the details
   *
   * @param isXenialEnabled - Whether Xenial is enabled for the brand
   * @param xenialSyncControlValue - The value of the xenial sync control toggle
   * @param xenialDetails - Xenial details from the form containing sync settings
   * @returns boolean indicating if POS association should be created
   */
  shouldCreateXenialPosAssociation(
    isXenialEnabled: boolean,
    xenialSyncControlValue: boolean,
    xenialDetails: any,
  ): boolean {
    if (!isXenialEnabled) {
      return false;
    }

    if (!xenialSyncControlValue) {
      return false;
    }

    if (!xenialDetails || !Object.keys(xenialDetails).length) {
      return false;
    }

    return xenialDetails.enableXenialSync === true;
  }

  /**
   * Returns the existing Xenial POS association for the catalog entry, or null if not found
   * @param associationList - The list of existing associations to check
   * @param dataGroupId - The data group ID
   * @param catalogEntryId - The catalog entry ID
   * @param rawPosValue - The raw POS value to check
   * @param associationType - The association type to check for
   * @returns Association object or null
   */
  getExistingXenialAssociation(
    associationList: Association[],
    dataGroupId: string,
    catalogEntryId: string,
    rawPosValue: string,
    associationType: AssociationType,
  ): Association | null {
    return (
      associationList.find(
        (assoc) =>
          assoc.catalogEntryId === catalogEntryId &&
          assoc.dataGroupId === dataGroupId &&
          assoc.rawPosValue === rawPosValue &&
          assoc.associationType === associationType,
      ) || null
    );
  }

  /**
   * Checks if there's already an existing Xenial POS association for the catalog entry
   * @param associationList - The list of existing associations to check
   * @param dataGroupId - The data group ID
   * @param catalogEntryId - The catalog entry ID
   * @param rawPosValue - The raw POS value to check
   * @param associationType - The association type to check for
   * @returns boolean indicating if association exists
   */
  checkExistingXenialAssociation(
    associationList: Association[],
    dataGroupId: string,
    catalogEntryId: string,
    rawPosValue: string,
    associationType: AssociationType,
  ): boolean {
    return !!this.getExistingXenialAssociation(
      associationList,
      dataGroupId,
      catalogEntryId,
      rawPosValue,
      associationType,
    );
  }

  /**
   * Creates a Xenial POS association for the catalog entry
   *
   * This method handles the complete workflow for creating POS associations:
   * 1. Validates that a Xenial data group is available
   * 2. Checks for existing associations to prevent duplicates
   * 3. For modifiers (INSTRUCTION lookup type), uses the cached "Add" instruction ID
   * 4. Creates a new association if none exists
   * 5. Handles errors gracefully to avoid breaking the parent save operation
   *
   * @param brandId - The brand/tenant ID
   * @param catalogEntryId - The catalog entry ID (for products) or will be replaced with "Add" instruction ID for modifiers
   * @param xenialDetails - Xenial details from form containing rawPosValue
   * @param xenialDataGroupId - The Xenial data group ID for the tenant
   * @param associationLookupType - The type of catalog entry (PRODUCT for products, INSTRUCTION for modifiers)
   * @param associationType - The specific association type for the POS mapping
   * @param associationList - Current list of associations to check for duplicates
   * @param associatedCatalogEntryId - Optional associated catalog entry ID (used for modifier instructions)
   * @returns Observable that emits the created association or null if skipped/failed
   */
  createXenialPosAssociation(
    brandId: string,
    catalogEntryId: string,
    xenialDetails: any,
    xenialDataGroupId: string,
    associationLookupType: AssociationLookupType,
    associationType: AssociationType,
    associationList: Association[],
    associatedCatalogEntryId?: string,
  ): Observable<any> {
    if (!xenialDataGroupId) {
      return of(null);
    }

    const rawPosValue = xenialDetails.rawPosValue || '';
    const associationExists = this.checkExistingXenialAssociation(
      associationList,
      xenialDataGroupId,
      catalogEntryId,
      rawPosValue,
      associationType,
    );
    if (associationExists) {
      return of(null); // Association already exists, skip creation
    }

    let finalCatalogEntryId = catalogEntryId;
    if (associationLookupType === AssociationLookupType.INSTRUCTION) {
      const addInstructionId = this.getCachedAddInstructionId();
      if (addInstructionId) {
        finalCatalogEntryId = addInstructionId;
      }
    }

    const association: Association = {
      associationLookupType: associationLookupType,
      tenantId: brandId,
      catalogEntryId: finalCatalogEntryId,
      rawPosValue: rawPosValue,
      dataGroupId: xenialDataGroupId,
      associationType: associationType,
    };

    if (associatedCatalogEntryId) {
      association.associatedCatalogEntryId = associatedCatalogEntryId;
    }

    return this.associationService.create(association).pipe(
      catchError((error) => {
        return of(null);
      }),
    );
  }

  /**
   * Gets all Xenial catalogs for sync status information
   * @param brandId - The brand/tenant ID
   * @returns Observable that emits the paginated Xenial catalogs
   */
  getXenialCatalogs(brandId: string): Observable<any> {
    const params = { 'Active-Tenant-Identifier': brandId };
    return this.generatedXenialService.xenialCatalogsGet(params).pipe(
      catchError((error) => {
        return throwError(error);
      }),
    );
  }

  /**
   * Loads Xenial sync status for a specific catalog entry
   * @param brandId - The brand/tenant ID
   * @param catalogEntryId - The catalog entry ID to check sync status for
   * @param unsubscribe$ - Subject to handle component unsubscription
   * @returns Observable that emits XenialSyncStatusResult
   */
  loadXenialSyncStatus(
    brandId: string,
    catalogEntryId: string,
    unsubscribe$: Subject<void>,
  ): Observable<XenialSyncStatusResult> {
    if (!catalogEntryId || !brandId) {
      return of({
        status: null,
        lastSyncTime: null,
        isLoading: false,
      });
    }

    return this.getXenialCatalogs(brandId).pipe(
      takeUntil(unsubscribe$),
      map((response) => {
        if (response?._embedded?.xenial) {
          const currentItem = response._embedded.xenial.find(
            (item: any) => item.catalogEntryId === catalogEntryId,
          );

          if (currentItem) {
            return {
              status: currentItem.externalLastUpdatedStatus || 'UNKNOWN',
              lastSyncTime: currentItem.externalLastUpdatedTime || null,
              isLoading: false,
            };
          }
        }

        return {
          status: 'UNKNOWN',
          lastSyncTime: null,
          isLoading: false,
        };
      }),
      catchError(() => {
        return of({
          status: 'ERROR',
          lastSyncTime: null,
          isLoading: false,
        });
      }),
    );
  }

  /**
   * Gets the appropriate icon name for Xenial sync status
   * @param status - The sync status string
   * @param isLoading - Whether the status is currently loading
   * @returns Icon name string
   */
  getXenialSyncStatusIcon(status: string | null, isLoading: boolean): string {
    if (isLoading) {
      return 'refresh';
    }
    switch (status) {
      case 'SUCCESS':
        return 'check';
      case 'ERROR':
      case 'FAILED':
      case 'FAIL':
        return 'warning';
      default:
        return 'help';
    }
  }

  /**
   * Gets the appropriate icon class for Xenial sync status
   * @param status - The sync status string
   * @param isLoading - Whether the status is currently loading
   * @returns Icon class string
   */
  getXenialSyncStatusIconClass(status: string | null, isLoading: boolean): string {
    if (isLoading) {
      return 'tce-icon-refresh spinning';
    }
    switch (status) {
      case 'SUCCESS':
        return 'tce-icon-circle-check passed';
      case 'ERROR':
      case 'FAILED':
      case 'FAIL':
        return 'tce-icon-warning failed';
      default:
        return 'tce-icon-help unknown';
    }
  }

  /**
   * Gets the appropriate CSS class for Xenial sync status
   * @param status - The sync status string
   * @returns CSS class string
   */
  public getXenialSyncStatusClass(status: string | null): string {
    switch (status) {
      case 'SUCCESS':
        return 'sync-success';
      case 'ERROR':
      case 'FAILED':
      case 'FAIL':
        return 'sync-error';
      default:
        return 'sync-unknown';
    }
  }

  /**
   * Gets the display text for Xenial sync status
   * @param status - The sync status string
   * @param isLoading - Whether the status is currently loading
   * @returns Translated status text
   */
  getXenialSyncStatusText(status: string | null, isLoading: boolean): string {
    if (isLoading) {
      return this.translateService.instant('catalog.common.xenialDetails.syncing');
    }

    switch (status) {
      case 'SUCCESS':
        return this.translateService.instant('catalog.common.xenialDetails.synced');
      case 'ERROR':
      case 'FAILED':
      case 'FAIL':
        return this.translateService.instant('catalog.common.xenialDetails.errorSyncing');
      default:
        return this.translateService.instant('catalog.common.xenialDetails.pendingStatus');
    }
  }

  /**
   * Gets the tooltip text for Xenial sync status
   * @param status - The sync status string
   * @param lastSyncTime - The last sync timestamp
   * @param isLoading - Whether the status is currently loading
   * @returns Translated tooltip text
   */
  public getXenialSyncTooltip(
    status: string | null,
    lastSyncTime: string | null,
    isLoading: boolean,
  ): string {
    if (isLoading) {
      return this.translateService.instant('catalog.common.xenialDetails.loadingStatus');
    }

    if (!lastSyncTime) {
      return this.translateService.instant('catalog.common.xenialDetails.noSyncData');
    }

    const formattedTime = new Date(lastSyncTime).toLocaleString();

    switch (status) {
      case 'SUCCESS':
        return this.translateService.instant('catalog.common.xenialDetails.lastSynced', {
          time: formattedTime,
        });
      case 'ERROR':
      case 'FAILED':
      case 'FAIL':
        return this.translateService.instant('catalog.common.xenialDetails.erroredAt', {
          time: formattedTime,
        });
      default:
        return this.translateService.instant('catalog.common.xenialDetails.pendingStatus');
    }
  }

  /**
   * Gets the list of store prices for a catalog entry (Xenial Stores tab)
   */
  getStorePrices(brandId: string, catalogEntryId: string): Observable<any> {
    // Real implementation: fetch prices from ce-experience-admin/catalogs/prices/{catalogId}
    const params: CatalogsPricesCatalogEntryIdGet$Params = {
      catalogEntryId,
      'Active-Tenant-Identifier': brandId,
    };
    return this.catalogPricesService.catalogsPricesCatalogEntryIdGet(params).pipe(
      map((response: any) => {
        // Response shape: { prices: [{ storeNumber, price, defaultPrice, name, available }] }
        if (response) {
          return response._embedded.menuPrice;
        }
        return [];
      }),
    );
  }

  /**
   * Updates store prices for Xenial integration
   * @param updates Array of XenialPricingUpdateRequest objects
   * @param brandId The brand/tenant ID
   * @returns Observable<any> (API response)
   */
  updateStorePrices(updates: PricePutRequests, brandId: string): Observable<any> {
    const params: CatalogsPricesPut$Params = {
      'Active-Tenant-Identifier': brandId,
      body: updates,
      integrations: 'xenial',
    };
    return this.catalogPricesService.catalogsPricesPut(params).pipe(
      map((response: any) => {
        // Response shape: { prices: [{ storeNumber, price, defaultPrice, name, available }] }
        if (response && Array.isArray(response.prices)) {
          return response.prices;
        }
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }
}
